import { useCallback, useState } from "react";
import { useFlag } from "../../../hooks/useFlag";
import { useDispatch } from "react-redux";
import { refreshCredentialsAction, resetAuth, updateAuth } from "../authSlice";
import { login } from "../../auth0";
import { useRouter } from "next/router";
import { UserRole } from "../../../generated/graphql";
import useTranslateMessage from "../../../hooks/useTranslateMessage";

const useLogin = () => {
  const t = useTranslateMessage();
  const { push } = useRouter();
  const dispatch = useDispatch();

  const [isLoading, onLoadingStart, onLoadingEnd] = useFlag();

  const [error, setError] = useState("");

  const DEFAULT_ERROR_MESSAGE = t({ id: "useLogin-DEFAULT_ERROR_MESSAGE" });
  const NO_PERMISSION_ERROR_MESSAGE = t({ id: "useLogin-11,3" });

  const handleLogin = useCallback(
    (email: string, password: string) => {
      onLoadingStart();

      login({ email, password })
        .then((auth) => {
          if (!auth.roles || auth.roles.includes(UserRole.OrderCreator)) {
            setError(NO_PERMISSION_ERROR_MESSAGE);
            return dispatch(resetAuth());
          }

          setError("");

          dispatch(updateAuth(auth));

          // schedule a credentials refresh before they expire
          if (auth.expire) {
            setTimeout(
              () => dispatch(refreshCredentialsAction(auth)),
              (auth.expire - 10) * 1000,
            );
          }

          if (auth.roles?.includes(UserRole.CatalogManager)) {
            push("/catalog");
          } else {
            push("/requisitions");
          }
        })
        .catch(() => {
          setError(DEFAULT_ERROR_MESSAGE);

          dispatch(resetAuth());
        })
        .finally(onLoadingEnd);
    },
    [
      DEFAULT_ERROR_MESSAGE,
      NO_PERMISSION_ERROR_MESSAGE,
      dispatch,
      onLoadingEnd,
      onLoadingStart,
      push,
    ],
  );

  return { isLoading, error, handleLogin, setError };
};

export { useLogin };
